import { useState, useEffect, useMemo, useContext } from "react"
import { useSelector } from "react-redux"
import { PLOTTYPE, TOOLS } from "./chartConfig"
import { resetUiSpan } from "components/atoms/chart/logic/chartSpan"
import { ChartFlagContext } from "components/atoms/chart"
import { pickTarget } from "redux/ducks/Chart"
import _ from "lodash"

interface props {
  canvasID: string
  isInfoValid?: boolean
  miniChartFlag?: boolean
}

interface Window {
  spinnerService: any
  Logic: any
  IntelliFactory: any
  Trading: any
}

declare var window: Window

// plotTypeをバインドしておくクラス。バインドしておかないと、mouseMove内で判定できない。
class PlotTypeController {
  plotType: {
    id: number
    displayTitle: string
    key: string
  }
  constructor() {
    this.plotType = null
  }
  get = () => {
    return this.plotType
  }
  set = (data: { id: number; displayTitle: string; key: string }) => {
    this.plotType = data
  }
}

class SymbolController {
  symbol: object
  constructor() {
    this.symbol = {}
  }
  isEmpty = () => {
    return _.isEmpty(this.symbol)
  }
  set = (data: object) => {
    this.symbol = data
  }
}

class DrawModeController {
  drawMode: boolean
  constructor() {
    this.drawMode = null
  }
  set = (data: boolean) => {
    this.drawMode = data
  }
}

class MouseLockController {
  lock: boolean
  deactivate: boolean
  constructor() {
    this.lock = false
    this.deactivate = false
  }
  set = (lock: boolean) => {
    this.lock = lock
  }
}

class LoupeController {
  loupe: boolean
  constructor() {
    this.loupe = false
  }
  set = (lock: boolean) => {
    this.loupe = lock
  }
}

const plotTypeController = new PlotTypeController()
const symbolController = new SymbolController()
const drawModeController = new DrawModeController()
const mouseLockController = new MouseLockController()
const loupeController = new LoupeController()

const useChartCursor = (props: props): Array<number | object> => {
  const { canvasID } = props
  const chartFlagContext = useContext(ChartFlagContext)
  // Selector
  const plotTypeSelector = (state: any) => state.chart[canvasID].plotType
  const figureTypeSelector = (state: any) => state.chart[canvasID].figureType
  const toolSelector = (state: any) => state.chart[canvasID].tools
  const providersSelector = (state: any) => state.chart[canvasID].providers
  const symbolSelector = (state: any) => state.chart[canvasID].symbol
  const plotType = useSelector(plotTypeSelector)
  const figureType = useSelector(figureTypeSelector)

  const providers = useSelector(providersSelector)
  const symbol = useSelector(symbolSelector)
  const tool = useSelector(toolSelector)
  const [_provider, _controller, _procs] = useMemo(() => {
    return providers
  }, [providers])

  const drawMode = useMemo(() => {
    return !_.isEmpty(figureType)
  }, [figureType])
  const [mouseValue, setMouseValue] = useState(null)
  const [cursor, setCursor] = useState("default")
  let mousePoint: MouseEvent | null = null
  plotTypeController.set(plotType)
  const [deactivate, setDeactivate] = useState(mouseLockController.deactivate)

  const addChartCursor = (): void => {
    if (!_controller || props.miniChartFlag) return
    const canvas = document.getElementById(canvasID)
    let tMgr = window.Trading.Chart.Proto.Controller.TouchMgr.New(_controller)
    /**
     * マウス
     */
    canvas.onmousedown = e => {
      if (symbolController.isEmpty() || mouseLockController.deactivate) return
      if (mouseLockController.lock) {
        mouseLockController.set(false)
        mousePoint = null
      } else {
        mouseLockController.set(false)
        mousePoint = e
      }
      // 図形コマンド実行時はロック処理しない
      if (drawModeController.drawMode) {
        mouseLockController.set(false)
        mousePoint = null
      }
      // マウスイベントをチャートに通知
      let ae = adjustXY(e)
      _controller.OnPointerDown.call(null, [2, 0, ae.mouseX, ae.mouseY])
      return false
    }

    canvas.onmousemove = e => {
      if (mouseLockController.deactivate) return
      if (mouseLockController.lock) {
        // ロック中なので、イベント処理停止させる
        let se = adjustXY(mousePoint)
        _controller.OnPointerMove.call(null, [2, 0, se.mouseX, se.mouseY])
        return false
      }

      // マウスイベントをチャートに通知
      let ae = adjustXY(e)
      if (plotTypeController.get().id !== PLOTTYPE[3].id) {
        const mouseValue = _controller.Chart.GetValueOnY(ae.mouseY)
        setMouseValue(mouseValue)
      }
      const me = _controller.OnPointerMove.call(null, [
        2,
        0,
        ae.mouseX,
        ae.mouseY,
      ])
      let cursor = "default"
      if (me["$0"]) {
        switch (me["$0"]["$"]) {
          case 3:
            cursor = "crosshair"
            break
          case 14:
            cursor = "s-resize"
            break
          case 15:
            cursor = "e-resize"
            break
          case 27:
            cursor = "pointer"
            break
          default:
        }
      }
      setCursor(cursor)
      // chartFlagContext.scrollFlag.setFlagはtech-chartにしかない。
      // if (_.get(chartFlagContext, ["scrollFlag", "setFlag"]))
      //   chartFlagContext.scrollFlag.setFlag(true)
      return false
    }
    canvas.onmouseleave = e => {
      // 描画コマンド実行中は、画面からマウスが離れたら、図形作成実行する
      if (!drawModeController.drawMode) {
        let ae = adjustXY(e)
        _controller.OnPointerUp.call(null, [2, 0, ae.mouseX, ae.mouseY])
      }
      return false
    }
    canvas.onmouseup = e => {
      if (mousePoint) {
        // クリックが同一点かどうか
        if (
          Math.abs(mousePoint.x - e.x) < 2 &&
          Math.abs(mousePoint.y - e.y) < 2
        ) {
          mouseLockController.set(true)
          let lp = adjustXY(mousePoint)
          let me = _controller.OnPointerUp.call(null, [
            2,
            0,
            lp.mouseX,
            lp.mouseY,
          ])

          // スクロールバー回りクリック時に、画面ロック処理するかどうかの確認
          if (!me["$0"] || cursor === "e-resize") {
            // スクロール領域上クリック or スクロールバーハンドラーでのリサイズ中なので、画面ロック処理しない
            mouseLockController.set(false)
            mousePoint = null
            if (!me["$0"]) {
              // スクロール領域クリックは、期間変更なので、本数もしくは期間のUI更新
              resetUiSpan(canvasID, _controller)
            }
          }
          if (loupeController.loupe) {
            mouseLockController.set(false)
            _controller.OnPointerMove.call(null, [2, 0, lp.mouseX, lp.mouseY])
            _controller.Chart.Draw()
          }
          if (!loupeController.loupe) {
            resetUiSpan(canvasID, _controller)
          }

          return false
        }
      }
      // マウスイベントをチャートに通知
      let ae = adjustXY(e)
      _controller.OnPointerUp.call(null, [2, 0, ae.mouseX, ae.mouseY])
      if (!mouseLockController.lock) {
        // 図形コマンドの時以外は、期間変更なので、本数もしくは期間のUI更新
        resetUiSpan(canvasID, _controller)
      }
      if (loupeController.loupe) {
        mouseLockController.set(false)
        _controller.OnPointerMove.call(null, [2, 0, ae.mouseX, ae.mouseY])
        _controller.Chart.Draw()
      }
      return false
    }
    /**
     * タッチデバイス
     */
    canvas.addEventListener(
      "touchstart",
      e => {
        tMgr.TouchStart(
          window.IntelliFactory.WebSharper.List.ofArray(
            toTouchesPrm(e.targetTouches)
          )
        )
      },
      false
    )
    canvas.addEventListener(
      "touchmove",
      e => {
        tMgr.TouchMove(
          window.IntelliFactory.WebSharper.List.ofArray(
            toTouchesPrm(e.targetTouches)
          )
        )
        e.preventDefault()
      },
      false
    )
    canvas.addEventListener(
      "touchend",
      e => {
        tMgr.TouchEnd(
          window.IntelliFactory.WebSharper.List.ofArray(
            toTouchesPrm(e.targetTouches)
          )
        )
      },
      false
    )
    canvas.addEventListener(
      "touchcancel",
      () => {
        tMgr.Cancel()
      },
      false
    )
  }

  const adjustXY = (e: any) => {
    let rect = e.target.getBoundingClientRect()
    return {
      mouseX: e.clientX - rect.left,
      mouseY: e.clientY - rect.top,
    }
  }

  const toTouchesPrm = (touches: any) => {
    let len = touches.length
    let res = new Array(len)
    for (let i = 0; i < len; i++) {
      let touch = touches[i]
      res[i] = {
        Id: touch.identifier,
        X: touch.clientX - touch.target.offsetLeft,
        Y: touch.clientY - touch.target.offsetTop,
      }
    }
    return res
  }

  const setDeactivateTime = () => {
    setDeactivate(true)
    setTimeout(() => {
      setDeactivate(false)
    }, 200)
  }

  useEffect(() => {
    addChartCursor()
  }, [_controller])

  useEffect(() => {
    plotTypeController.set(plotType)
  }, [plotType])

  useEffect(() => {
    symbolController.set(symbol)
  }, [symbol])

  useEffect(() => {
    drawModeController.set(!!drawMode)
  }, [drawMode])

  useEffect(() => {
    setDeactivateTime()
  }, [figureType])

  useEffect(() => {
    mouseLockController.deactivate = deactivate
    if (deactivate) return setCursor("wait")
    setCursor("default")
  }, [deactivate])

  useEffect(() => {
    if (!!_.find(tool, _.pick(TOOLS[4], pickTarget))) loupeController.set(true)
    if (!_.find(tool, _.pick(TOOLS[4], pickTarget))) loupeController.set(false)
  }, [tool])

  return [mouseValue, cursor]
}

export default useChartCursor
